import { useEffect, useState } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Save, AlertCircle, Users } from 'lucide-react'

interface UserLimitsSettings {
  isActive: boolean
  maxLettersPerHour: number
}

const DEFAULT_SETTINGS: UserLimitsSettings = {
  isActive: false,
  maxLettersPerHour: 5,
}

export default function UserLimitsTab() {
  const [settings, setSettings] = useState<UserLimitsSettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      const docRef = doc(db, 'userLimits', 'settings')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data() as UserLimitsSettings
        setSettings({
          isActive: data.isActive || false,
          maxLettersPerHour: data.maxLettersPerHour || 5,
        })
      } else {
        // Create default document
        await setDoc(docRef, DEFAULT_SETTINGS)
      }
    } catch (error) {
      console.error('Error loading settings:', error)
      setMessage({ type: 'error', text: 'Ayarlar yüklenirken hata oluştu' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage(null)

    try {
      const docRef = doc(db, 'userLimits', 'settings')
      await setDoc(docRef, settings, { merge: true })
      setMessage({ type: 'success', text: 'Ayarlar başarıyla kaydedildi' })
    } catch (error) {
      console.error('Error saving settings:', error)
      setMessage({ type: 'error', text: 'Ayarlar kaydedilirken hata oluştu' })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = () => {
    setSettings({
      ...settings,
      isActive: !settings.isActive,
    })
  }

  const handleMaxLettersChange = (value: number) => {
    if (value >= 1 && value <= 100) {
      setSettings({
        ...settings,
        maxLettersPerHour: value,
      })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {message && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <AlertCircle className="w-5 h-5" />
          <span>{message.text}</span>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Kullanıcı Limitleri Nasıl Çalışır?</h4>
            <p className="text-sm text-blue-800">
              Bu ayar aktif edildiğinde, her kullanıcı belirlediğiniz saatte maksimum mektup sayısı kadar mektup gönderebilir.
              Limit aşıldığında, kullanıcıya uygun bir hata mesajı gösterilir.
            </p>
            <p className="text-sm text-blue-800 mt-2">
              <strong>Örnek:</strong> Saatte maksimum 5 mektup seçildiğinde, bir kullanıcı 1 saat içinde en fazla 5 mektup gönderebilir.
            </p>
          </div>
        </div>
      </div>

      {/* Active Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Kullanıcı Limitlerini Aktif Et</h3>
            <p className="text-sm text-gray-500 mt-1">
              Aktif edildiğinde, kullanıcılar saatte belirlenen maksimum mektup sayısı kadar mektup gönderebilir
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.isActive}
              onChange={handleToggleActive}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>

      {/* Max Letters Per Hour */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Saatte Maksimum Mektup Sayısı</h3>
        <p className="text-sm text-gray-500 mb-6">
          Bir kullanıcının 1 saat içinde gönderebileceği maksimum mektup sayısını belirleyin.
        </p>

        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maksimum Mektup Sayısı
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleMaxLettersChange(settings.maxLettersPerHour - 1)}
                disabled={settings.maxLettersPerHour <= 1 || !settings.isActive}
                className="w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <span className="text-lg font-semibold text-gray-600">−</span>
              </button>
              
              <input
                type="number"
                min="1"
                max="100"
                value={settings.maxLettersPerHour}
                onChange={(e) => handleMaxLettersChange(parseInt(e.target.value) || 1)}
                disabled={!settings.isActive}
                className="w-24 px-4 py-2 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
              
              <button
                onClick={() => handleMaxLettersChange(settings.maxLettersPerHour + 1)}
                disabled={settings.maxLettersPerHour >= 100 || !settings.isActive}
                className="w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <span className="text-lg font-semibold text-gray-600">+</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Minimum: 1, Maksimum: 100
            </p>
          </div>
          
          <div className="flex-1">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Mevcut Ayar</div>
              <div className="text-3xl font-bold text-gray-900">
                {settings.maxLettersPerHour}
              </div>
              <div className="text-sm text-gray-500 mt-1">mektup / saat</div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  )
}
